import {
  MdAttachFile,
  MdCalendarToday,
  MdDataObject,
  MdNumbers,
  MdTextFields,
  MdToggleOn,
} from "react-icons/md";
import { IconType } from "react-icons";

export type FieldType =
  | "text"
  | "number"
  | "boolean"
  | "date"
  | "object"
  | "file";

export interface FieldTypeInfo {
  icon: IconType;
  color: string;
  bgColor: string;
  borderColor: string;
  label: string;
}

export const getFieldTypeInfo = (value: unknown): FieldTypeInfo => {
  if (typeof value === "boolean") {
    return {
      icon: MdToggleOn,
      color: "text-gray-600",
      bgColor: "bg-gray-50",
      borderColor: "border-gray-200",
      label: "Boolean",
    };
  }
  if (typeof value === "number") {
    return {
      icon: MdNumbers,
      color: "text-green-600",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      label: "Number",
    };
  }
  if (Array.isArray(value) && value.length > 0 && value[0]?.via_sum) {
    return {
      icon: MdAttachFile,
      color: "text-gray-600",
      bgColor: "bg-gray-50",
      borderColor: "border-gray-200",
      label: "File",
    };
  }
  if (Array.isArray(value) && value.length === 0) {
    return {
      icon: MdAttachFile,
      color: "text-gray-600",
      bgColor: "bg-gray-50",
      borderColor: "border-gray-200",
      label: "File",
    };
  }
  if (typeof value === "object" && value !== null) {
    return {
      icon: MdDataObject,
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200",
      label: "Object",
    };
  }
  if (
    value instanceof Date ||
    (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value))
  ) {
    return {
      icon: MdCalendarToday,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      label: "Date",
    };
  }
  return {
    icon: MdTextFields,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    label: "Text",
  };
};

export const detectFieldType = (
  value: unknown,
  allowedTypes: FieldType[] = []
): FieldType => {
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number") return "number";

  if (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(
      (item) =>
        typeof item === "object" &&
        item !== null &&
        "file_name" in item &&
        "extension" in item &&
        "via_sum" in item &&
        item.via_sum === true
    )
  ) {
    return "file";
  }

  if (
    typeof value === "object" &&
    allowedTypes.includes("file") &&
    Array.isArray(value) &&
    value.length === 0
  ) {
    return "file";
  }

  if (typeof value === "object" && value !== null) return "object";
  if (
    value instanceof Date ||
    (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value))
  )
    return "date";

  return "text";
};

export const getFieldValuePreview = (
  value: unknown,
  t: (key: string, options?: Record<string, unknown>) => string,
  maxLength = 100
): string => {
  if (typeof value === "string") {
    return value.length > maxLength
      ? value.substring(0, maxLength) + "..."
      : value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    if (value.length > 0 && value[0]?.via_sum) {
      return t("common.fileCount", { count: value.length });
    }
    if (value.length === 0) {
      return t("common.noFiles");
    }
    const jsonStr = JSON.stringify(value);
    return jsonStr.length > maxLength
      ? jsonStr.substring(0, maxLength) + "..."
      : jsonStr;
  }
  if (typeof value === "object" && value !== null) {
    const jsonStr = JSON.stringify(value);
    return jsonStr.length > maxLength
      ? jsonStr.substring(0, maxLength) + "..."
      : jsonStr;
  }
  return "-";
};

export const sanitizeAndTrim = (text: string, allowedChars = "_-"): string => {
  const disallowedChars = `[^a-zA-Z0-9${allowedChars.replace(
    /[-[\]{}()*+?.,\\^$|#]/g,
    "\\$&"
  )}]`;
  const regex = new RegExp(disallowedChars, "g");
  return text.replace(regex, "").trim();
};

export const validateName = (
  name: string
): {
  isValid: boolean;
  errorKey?: string;
  errorParams?: Record<string, string>;
} => {
  if (!name || name.trim() === "") {
    return { isValid: false, errorKey: "validation.nameEmpty" };
  }

  if (name.includes(" ")) {
    return {
      isValid: false,
      errorKey: "validation.nameNoSpaces",
    };
  }

  if (name.includes(".")) {
    return {
      isValid: false,
      errorKey: "validation.nameNoDots",
    };
  }

  const invalidCharsRegex = /[^a-zA-Z0-9_-]/g;
  const invalidChars = name.match(invalidCharsRegex);

  if (invalidChars && invalidChars.length > 0) {
    const uniqueInvalidChars = Array.from(new Set(invalidChars)).join(", ");
    return {
      isValid: false,
      errorKey: "validation.nameInvalidChars",
      errorParams: { chars: uniqueInvalidChars },
    };
  }

  return { isValid: true };
};

export const isDuplicateName = (
  name: string,
  existingNames: string[],
  excludeName?: string
): boolean => {
  const lowerName = name?.toLowerCase();
  return existingNames.some((existing) => {
    if (excludeName && existing.toLowerCase() === excludeName.toLowerCase()) {
      return false;
    }
    return existing.toLowerCase() === lowerName;
  });
};

export const cleanEmptyFields = (
  data: Record<string, unknown>
): Record<string, unknown> => {
  return Object.entries(data).reduce((acc, [key, value]) => {
    if (key && key.trim() !== "") {
      acc[key] = value;
    }
    return acc;
  }, {} as Record<string, unknown>);
};
